import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePackages } from '../../data/usePackages';
import { useStudents } from '../../data/useStudents';
import { useFamilies } from '../../data/useFamilies';
import { useEvents } from '../../data/useEvents';
import { useAttendance } from '../../data/useAttendance';
import useNames from '../../hooks/useNames';
import { attendanceWindow } from '../../hooks/useStudentAttendance';
import {
  bookedLessonsOf, convertibleLessonsOf, forecastHorizon,
} from '../../hooks/useStudentPackageForecast';
import { todayISO } from '../../domain/clock';
import { formatClock } from '../../domain/packageTemplates';
import { packagesTotalMinutes, unlistedStudentsText } from '../../domain/packageSale';
import { useSettings } from '../../data/useSettings';
import Button from '../Button';
import EmptyState from '../EmptyState';
import ReadFailed from '../ReadFailed';
import { READING_WORDING as RW, reading } from '../../domain/reading';
import SellPackageWizard from './SellPackageWizard';
import s from './packages.module.css';
import t from '../SimpleTable.module.css';

// `Packages → Package Balances` (#369).
//
// ── What MMS shows (#181) ──────────────────────────────────────────────────
//
// A grid: `Student | Teacher | Family Contact | Upcoming Lesson Time |
// Available Time | Total Time`, with `+ Add Package` on it. Three figures at
// studio level where the student rail shows two — `Total Time` is the one the
// rail does not.
//
// ── The three columns that are not simply theirs ──────────────────────────
//
// `Teacher` is dropped: this app has no teacher entity — one studio, one owner
// — the substitution `SellPackageWizard` makes for the ledger row's `Lesson -
// <template> - <teacher>`. A column repeating the owner's name on every row
// answers nothing.
//
// `Family Contact` is the contact the family is billed to,
// `useFamilies().billingContactFor`, the resolver the invoice uses, written in
// the Parent Name Format as Family Accounts writes the same person; a family
// with nobody to bill shows the dash there and here (#632; FamilyList.jsx
// `BilledContact`).
//
// Which students have a row is ours: whether MMS lists a student who never
// bought a package is unrecorded. A row of zeroes per student would make this a
// table about the roster, so a student with nothing purchased is left out, and
// the count under the table says how many that is.
//
// ── What it costs ───────────────────────────────────────────────────────────
//
// `Upcoming Lesson Time` is a forecast per student, which needs the student's
// occurrences expanded. Two things keep that from being a second calendar
// expansion:
//
//   * the window is `attendanceWindow`'s — the two dates `useStudentAttendance`
//     folds — because the window is a cache key in `useEvents`, so any other
//     pair would expand every series again. One call, whatever is on screen.
//   * only the students with a package are folded, so the per-student crossing
//     is bounded by how many packages a studio has sold rather than by its
//     roster.
//
// A studio with a hundred package-holding students still crosses the wasm
// boundary a hundred times on this tab; the fix, if it bites, is a fold that
// takes the whole roster at once rather than a narrower window.
//
// `Actions ▸ Apply Lesson Prices and Package Time`, the bulk operation on MMS's
// students list, is not here: what it applies, to which students and whether
// it writes is unrecorded.
//
// ── `+ Add Package` (#377) ─────────────────────────────────────────────────
//
// MMS's second entry point to the sale the student rail offers. The wizard
// needs a student and that student's convertible lessons, and with an empty
// `convertible` list it would sell a package that converts nothing, with
// nothing on step 2 to say so. So both are supplied here: `convertibleLessonsOf`
// is shared with the student rail, as `bookedLessonsOf` is (#369), since a
// copy admitting one lesson too many would convert a lesson already charged.
// The student is picked inside step 1, as MMS's step 1 is `student, template,
// start date`; this panel holds the selection because it is the only thing that
// can fold the lessons for whoever is picked.
//
// The picker's roster is every student, not only the rows in the table:
// selling a package is how a student gets into the table.
export default function PackageBalancesPanel() {
  const { students, loading: studentsLoading, failed: studentsFailed } = useStudents();
  const { byId: familyById, billingContactFor } = useFamilies();
  const { occurrencesBetween, priceFor } = useEvents();
  const { statusFor } = useAttendance();
  const {
    holdsPackage, forecastFor, sellPackage, loading: packagesLoading, failed: packagesFailed,
  } = usePackages();
  const { settings } = useSettings();
  const names = useNames();
  const navigate = useNavigate();

  const [selling, setSelling] = useState(false);
  // Held here rather than in the wizard, because the convertible list is a
  // fold over this student's occurrences and only this component has them.
  // See the wizard's header.
  const [sellTo, setSellTo] = useState('');

  const rows = useMemo(() => {
    const today = todayISO();
    const horizon = forecastHorizon(today);
    // One expansion for the whole tab, on the window `useStudentAttendance`
    // already asks for — see the header.
    const [from, to] = attendanceWindow(today);
    const occurrences = occurrencesBetween(from, to);

    return students
      // A student with nothing ever purchased has no row (see the header); the
      // count under the table says how many.
      //
      // `holdsPackage`, not "has package entries" (#384): `packageEntries` holds
      // every movement of the balance, so a consume written and voided by one
      // attendance mark would list a student who never bought a package. The
      // provider owns it because the live-entry half is the core's rule.
      .filter((student) => holdsPackage(student.id))
      .map((student) => {
        const history = occurrences
          .filter((o) => o.studentIds.includes(student.id))
          .map((o) => ({ ...o, status: statusFor(o.id, student.id) }));
        const { booked } = bookedLessonsOf({ history, student, priceFor });
        const forecast = forecastFor(student.id, booked, today, horizon);
        return { student, forecast };
      })
      .sort((a, b) => names.studentSortKey(a.student).localeCompare(names.studentSortKey(b.student)));
  }, [
    students, occurrencesBetween, statusFor, priceFor, holdsPackage, forecastFor, names,
  ]);

  // `Total Time` is what was sold, `purchased` off the packages rather than
  // `packageSummary().purchased`: the same sum, from the walk that produced the
  // two figures beside it, so the three come from one reading of the
  // documents. `packagesTotalMinutes` (src/domain/packageSale.js).
  const totalOf = packagesTotalMinutes;

  // Sorted the way every other list of students here is, so the dropdown reads
  // like the table above it.
  const roster = useMemo(
    () => [...students]
      .sort((a, b) => names.studentSortKey(a).localeCompare(names.studentSortKey(b)))
      .map((x) => ({ id: x.id, label: names.student(x) })),
    [students, names],
  );
  const sellStudent = students.find((x) => x.id === sellTo) || null;

  // The same window the table folds — one expansion for the tab, see the
  // header — narrowed to whoever is picked. Empty until somebody is, which is
  // why `Next` is disabled until then rather than the wizard opening on a
  // student with no lessons.
  const convertible = useMemo(() => {
    if (!sellStudent) return [];
    const [from, to] = attendanceWindow(todayISO());
    const history = occurrencesBetween(from, to)
      .filter((o) => o.studentIds.includes(sellStudent.id))
      .map((o) => ({ ...o, status: statusFor(o.id, sellStudent.id) }));
    return convertibleLessonsOf({ history, student: sellStudent, priceFor });
  }, [sellStudent, occurrencesBetween, statusFor, priceFor]);

  const closeWizard = () => { setSelling(false); setSellTo(''); };

  // `+ Add Package` is drawn whether or not the table has rows: it is on MMS's
  // tab, and a studio with no packages sold is the one that needs it. A
  // toolbar button above the table, where Invoices puts `+ New Invoice` and
  // Templates puts `+ Package Template` (#570).
  const toolbar = (
    <div className={s.bar}>
      <Button onClick={() => setSelling(true)}>+ Add Package</Button>
    </div>
  );

  const wizard = (
    <SellPackageWizard
      open={selling}
      students={roster}
      onStudentChange={setSellTo}
      studentId={sellTo}
      studentName={sellStudent ? names.student(sellStudent) : ''}
      familyId={sellStudent?.familyId}
      teacherName={settings.name || ''}
      convertible={convertible}
      todayISO={todayISO()}
      onClose={closeWizard}
      onSell={async (sale) => { await sellPackage(sale); }}
    />
  );

  // One place for the wizard, whichever of the three bodies is drawn: in each
  // branch's own fragment it would be a different slot, so the first sale —
  // the one that turns the empty state into the table — would unmount it
  // mid-sale and bring it back on step 1.
  const page = (body) => (
    <>
      {toolbar}
      {body}
      {wizard}
    </>
  );

  // A read that stopped before it answered (#647) says so, with Try again,
  // rather than reading for good.
  if ((studentsLoading && studentsFailed) || (packagesLoading && packagesFailed)) {
    return page(<ReadFailed />);
  }
  if (studentsLoading || packagesLoading) {
    return page(<div className={s.empty}>Reading the studio’s packages…</div>);
  }

  // The empty state every other list here uses — a headline and a line under
  // it, inside the table's border, with the action repeated as Invoices does
  // (#570). The phones say the same words as one line
  // (android/app/src/main/kotlin/studio/coda/app/ui/money/Packages.kt,
  // ios/CodaStudio/UI/Screens/PackageViews.swift).
  if (rows.length === 0) {
    return page(
      <div className={t.wrap}>
        <EmptyState
          reading={reading(!studentsLoading && !packagesLoading, rows.length)}
          title={RW['packages.balancesEmptyTitle']}
          description={RW['packages.balancesEmptyDescription']}
          actionLabel="+ Add Package"
          onAction={() => setSelling(true)}
        />
      </div>,
    );
  }

  return page(
    <>
      <div className={t.wrap}>
        <table className={t.table}>
          <thead>
            <tr>
              <th>Student</th>
              <th>Family Contact</th>
              <th>Upcoming Lesson Time</th>
              <th>Available Time</th>
              <th>Total Time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ student, forecast }) => (
              <tr key={student.id}>
                <td>
                  <button
                    type="button"
                    className={t.nameLink}
                    onClick={() => navigate(`/students/${student.id}`)}
                  >
                    {names.student(student)}
                  </button>
                </td>
                <td className={t.muted}>
                  {familyById(student.familyId) && billingContactFor(student.familyId)
                    ? names.familyContact(
                      familyById(student.familyId), billingContactFor(student.familyId),
                    )
                    : '-'}
                </td>
                <td>{formatClock(forecast.upcoming)}</td>
                <td>{formatClock(forecast.available)}</td>
                <td>{formatClock(totalOf(forecast))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* The absence, counted rather than left silent — see the header on which
          students have a row. */}
      {unlistedStudentsText(students.length - rows.length) ? (
        <div className={s.hint}>{unlistedStudentsText(students.length - rows.length)}</div>
      ) : null}
    </>,
  );
}
